import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import {
  getCompanyTimeRegistrationPolicy,
  getCostCentresCoordinatesStatusRequest,
  putCompanyPolicyUnit,
} from 'api/requests/company-time-registration-policy-requests';
import { parsePolicyError } from 'api/requests/time-registration-policy-requests';
import { mapCompanySaveError } from 'modules/companies/time-registration/helpers';
import {
  COMPANY_TIME_REGISTRATION_POLICY__CLEAR,
  COMPANY_TIME_REGISTRATION_POLICY__GET,
  COMPANY_TIME_REGISTRATION_POLICY__GET_COORDINATES_STATUS,
  COMPANY_TIME_REGISTRATION_POLICY__SAVE_UNIT,
  COMPANY_TIME_REGISTRATION_POLICY__SET_COORDINATES_STATUS,
  COMPANY_TIME_REGISTRATION_POLICY__SET_LOAD_ERROR,
  COMPANY_TIME_REGISTRATION_POLICY__SET_LOADING,
  COMPANY_TIME_REGISTRATION_POLICY__SET_UNIT,
  COMPANY_TIME_REGISTRATION_POLICY__SET_VIEW,
} from 'store/constants';
import { TIME_REGISTRATION_SETTINGS__FEATURE_NOT_ENABLED } from 'translations/constants';
import {
  CompanyPolicyLoadError,
  CompanyPolicySaveResult,
  CompanyTimeRegistrationPolicyView,
  CompanyUnitSetting,
  CompanyUnitState,
} from 'types/company/time-registration/ICompanyTimeRegistrationPolicy';
import { ICostCentresCoordinatesStatusResponse } from 'types/time-registration';
import { PolicyUnit } from 'types/time-registration-policy/ITimeRegistrationPolicy';

export const setCompanyPolicyViewAction = createAction<CompanyTimeRegistrationPolicyView>(
  COMPANY_TIME_REGISTRATION_POLICY__SET_VIEW,
);
export const setCompanyPolicyUnitAction = createAction<CompanyUnitState>(COMPANY_TIME_REGISTRATION_POLICY__SET_UNIT);
export const setCompanyPolicyLoadingAction = createAction<boolean>(COMPANY_TIME_REGISTRATION_POLICY__SET_LOADING);
export const setCompanyPolicyLoadErrorAction = createAction<CompanyPolicyLoadError>(
  COMPANY_TIME_REGISTRATION_POLICY__SET_LOAD_ERROR,
);
export const setCompanyPolicyCoordinatesStatusAction = createAction<ICostCentresCoordinatesStatusResponse | null>(
  COMPANY_TIME_REGISTRATION_POLICY__SET_COORDINATES_STATUS,
);
export const clearCompanyPolicyAction = createAction<void>(COMPANY_TIME_REGISTRATION_POLICY__CLEAR);

export const getCompanyTimeRegistrationPolicyAction = createAsyncThunk(
  COMPANY_TIME_REGISTRATION_POLICY__GET,
  async (companyId: number, thunkApi) => {
    thunkApi.dispatch(setCompanyPolicyLoadingAction(true));
    thunkApi.dispatch(setCompanyPolicyLoadErrorAction(null));
    try {
      const view = await getCompanyTimeRegistrationPolicy(companyId);
      thunkApi.dispatch(setCompanyPolicyViewAction(view));
    } catch (error) {
      const { key } = parsePolicyError(error);
      thunkApi.dispatch(
        setCompanyPolicyLoadErrorAction(
          key === TIME_REGISTRATION_SETTINGS__FEATURE_NOT_ENABLED ? 'featureNotEnabled' : 'failed',
        ),
      );
    }
    thunkApi.dispatch(setCompanyPolicyLoadingAction(false));
  },
);

export const getCompanyPolicyCoordinatesStatusAction = createAsyncThunk(
  COMPANY_TIME_REGISTRATION_POLICY__GET_COORDINATES_STATUS,
  async (companyId: number, thunkApi) => {
    try {
      const status = await getCostCentresCoordinatesStatusRequest(companyId);
      thunkApi.dispatch(setCompanyPolicyCoordinatesStatusAction(status));
    } catch {
      thunkApi.dispatch(setCompanyPolicyCoordinatesStatusAction(null));
    }
  },
);

interface SaveCompanyPolicyUnitArgs {
  companyId: number;
  unit: PolicyUnit;
  expected: CompanyUnitSetting;
  proposed: CompanyUnitSetting;
}

export const saveCompanyPolicyUnitAction = createAsyncThunk(
  COMPANY_TIME_REGISTRATION_POLICY__SAVE_UNIT,
  async (
    { companyId, unit, expected, proposed }: SaveCompanyPolicyUnitArgs,
    thunkApi,
  ): Promise<CompanyPolicySaveResult> => {
    try {
      const state = await putCompanyPolicyUnit(companyId, unit, expected, proposed);
      thunkApi.dispatch(setCompanyPolicyUnitAction(state));
      return { status: 'success', state };
    } catch (error) {
      return mapCompanySaveError(parsePolicyError(error));
    }
  },
);
